export type CrewKey =
  | "mason"
  | "carpenter"
  | "electrician"
  | "plumber"
  | "painter"
  | "roofer"
  | "service"
  | "siteManager"
  | "owner"
  | "tiler"
  | "joiner"
  | "facade"
  | "digger"
  | "developer";

export const CREW_ORDER: readonly CrewKey[] = [
  "mason",
  "carpenter",
  "electrician",
  "plumber",
  "painter",
  "roofer",
  "service",
  "siteManager",
  "owner",
  "tiler",
  "joiner",
  "facade",
  "digger",
  "developer",
];


/** Which of the two worlds a line speaks for. */
export type World = "survive" | "grow";

/** "mix" lets every figure speak for the world it fits best (home page). */
export type CrewMode = World | "mix";

export interface CrewLine {
  quote: string;
  answer: string;
}

export interface CrewEntry {
  label: string;
  home: World;
  /** Missing when the figure does not belong in that world's line-up. */
  survive?: CrewLine;
  grow?: CrewLine;
}

/** Slovak copy for the mock-up; moves to the locale files once approved. */
export const CREW: Record<CrewKey, CrewEntry> = {
  mason: {
    label: "Murár",
    home: "survive",
    survive: { quote: "Na výplaty zase nevyšlo.", answer: "Ukážeme vám dopredu, kedy budú chýbať peniaze. A kto vám ešte dlhuje." },
    grow: { quote: "Tri stavby naraz a neviem, kde mám ľudí.", answer: "Denný plán v mobile každého. Ráno nikto nevolá." },
  },
  carpenter: {
    label: "Tesár",
    home: "grow",
    survive: { quote: "Rezivo som zaplatil. Faktúru ešte nie.", answer: "Zálohovú faktúru pošlete ešte pred začatím. Rezivo zaplatí zákazník, nie vy." },
    grow: { quote: "Robotu mám na pol roka. Nestíham.", answer: "Partie, stavby a termíny na jednom mieste. Bez telefonovania." },
  },
  electrician: {
    label: "Elektrikár",
    home: "survive",
    survive: { quote: "Lacnejší ma prebil o tretinu.", answer: "Profesionálna ponuka ešte u zákazníka. Kto sa bojí fušera, vyberie si vás." },
    grow: { quote: "Kde zmizlo 40 metrov kábla?", answer: "Každý meter zapísaný na zákazku. Hneď vidíte, kde chýba." },
  },
  plumber: {
    label: "Inštalatér",
    home: "grow",
    survive: { quote: "Havária v noci. Faktúra nikdy.", answer: "Výjazd zapíšete v noci z mobilu. Ráno je z neho faktúra." },
    grow: { quote: "Montáže, servis, ľudia. Všetko v hlave.", answer: "Montáže aj servis v jednom pláne. Hlava je voľná." },
  },
  painter: {
    label: "Maliar",
    home: "survive",
    survive: { quote: "Dva mesiace čakám na peniaze.", answer: "Faktúra s QR kódom hneď po robote. A vidíte, kto vám dlhuje." },
    grow: { quote: "Partia maľuje a ja ani neviem kde.", answer: "Každý vidí svoju stavbu, vy všetky naraz." },
  },
  roofer: {
    label: "Pokrývač",
    home: "grow",
    survive: { quote: "Prší, partia stojí, výplaty bežia.", answer: "Uvidíte dopredu, ktorý týždeň bude ťažký. Máte čas zareagovať." },
    grow: { quote: "Dvaja na PN-ke a strecha stojí.", answer: "Dovolenky a PN-ky v kalendári. Vidíte, koho kam presunúť." },
  },
  service: {
    label: "Servisák",
    home: "survive",
    survive: { quote: "Tento týždeň nemám kam ísť.", answer: "Všetci zákazníci aj s históriou na jednom mieste. Viete, komu zavolať." },
    grow: { quote: "Šesť technikov a ja som dispečer.", answer: "Plán výjazdov, protokoly a faktúry z mobilu." },
  },
  siteManager: {
    label: "Stavbyvedúci",
    home: "grow",
    survive: { quote: "Investor platí o dva mesiace.", answer: "Vidíte, kto vám koľko dlhuje a kedy bude tesno." },
    grow: { quote: "Obrat rastie. Peňazí ubúda.", answer: "Zisk každej stavby, nie len obrat." },
  },
  owner: {
    label: "Majiteľ firmy",
    home: "grow",
    survive: { quote: "Investor mešká a výplaty mám pre 30 ľudí.", answer: "Kto vám koľko dlhuje, aj s upomienkami. Viete, komu volať ako prvému." },
    grow: { quote: "Štyridsať ľudí a neviem, ktorá stavba zarába.", answer: "Zisk každej stavby a kto kde dnes robí. Na jednom mieste." },
  },
  tiler: {
    label: "Obkladač",
    home: "survive",
    survive: { quote: "Drahší obklad som doplatil ja.", answer: "Zmenu zapíšete hneď so sumou. Do faktúry príde sama." },
    grow: { quote: "Tri byty naraz, materiál všade.", answer: "Materiál na konkrétny byt. Nič sa nestratí." },
  },
  joiner: {
    label: "Stolár",
    home: "grow",
    survive: { quote: "Kuchyňa hotová. Doplatok nikde.", answer: "Záloha pri objednávke, doplatok s QR kódom pri montáži." },
    grow: { quote: "Dielňa, montáže a ja som jeden.", answer: "Hodiny v dielni aj na montáži pri každej zákazke." },
  },
  facade: {
    label: "Fasádnik",
    home: "survive",
    survive: { quote: "Vzal som to pod cenu. Leasing nepočká.", answer: "Pozrite sa najprv, koľko vám dlhujú. Často je to viac, ako chýba na leasing." },
    grow: { quote: "Lešenie je zase na inej stavbe.", answer: "Lešenie a stroje evidované na stavbe, kde práve stoja." },
  },
  digger: {
    label: "Bagrista",
    home: "grow",
    survive: { quote: "Bager stál celý týždeň.", answer: "Prázdny týždeň uvidíte v pláne skôr. Máte čas zavolať zákazníkom." },
    grow: { quote: "Hodiny bagra nikto nezapísal.", answer: "Hodiny zapíšete na zákazku z mobilu. Nič nevypadne z faktúry." },
  },
  developer: {
    label: "Developer",
    home: "grow",
    grow: { quote: "Tri projekty, desať subdodávateľov. Rozpočet uteká.", answer: "Rozpočet, zmeny a skutočné náklady pri každom projekte. Odchýlku vidíte priebežne, nie pri kolaudácii." },
  },
};

/** A country's crew: who appears, in which order, and what each one says. */
export interface CrewSet {
  crew: Record<CrewKey, CrewEntry>;
  order: readonly CrewKey[];
}

/** The figures a line-up shows: everyone on the home page, otherwise those with a line for that world. */
export function crewFor(mode: CrewMode, set: CrewSet = { crew: CREW, order: CREW_ORDER }): CrewKey[] {
  return set.order.filter((key) => mode === "mix" || set.crew[key][mode] !== undefined);
}

/** The line a figure says in a line-up; on the home page it speaks for its own world. */
export function crewLine(key: CrewKey, mode: CrewMode, crew: Record<CrewKey, CrewEntry> = CREW): CrewLine {
  const world = mode === "mix" ? crew[key].home : mode;
  const line = crew[key][world];
  if (!line) throw new Error(`No ${world} line for ${key}`);
  return line;
}

/** The two landing pages the home page sends people to. */
/** Where a world page lives and how the home page names it. */
export interface WorldPage {
  href: string;
  door: string;
  title: string;
}

export const WORLD_PAGES: Record<World, WorldPage> = {
  survive: {
    href: "/ked-chyba-robota-a-peniaze/",
    door: "Toto som ja: chýba robota a peniaze",
    title: "Robil by som. Chýba robota a peniaze.",
  },
  grow: {
    href: "/ked-chyba-poriadok/",
    door: "Toto som ja: chýba poriadok",
    title: "Robota je. Chýba poriadok.",
  },
};
